export * from './context-memory-engine';
import {
  appendSmartMemory,
  buildPerfectPromptContext,
} from './context-memory-engine';

export type AiProvider = 'groq' | 'gemini' | 'openrouter';

export const getGroqApiKey = () => {
  if (typeof window !== 'undefined') {
    const fromStorage = localStorage.getItem('cao_oi_groq_api_key');
    if (fromStorage) return fromStorage;
  }
  return (
    (import.meta as any).env?.VITE_GROQ_API_KEY ||
    (import.meta as any).env?.GROQ_API_KEY ||
    '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'
  );
};

export function getActiveAiProvider(): AiProvider {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('cao_oi_ai_provider') as AiProvider;
    if (saved === 'gemini' || saved === 'openrouter' || saved === 'groq') {
      return saved;
    }
    const currentKey = localStorage.getItem('cao_oi_groq_api_key') || '';
    if (currentKey.startsWith('AIza')) return 'gemini';
    if (currentKey.startsWith('sk-or-')) return 'openrouter';
  }
  return 'groq';
}

export function setActiveAiProvider(prov: AiProvider) {
  if (typeof window !== 'undefined') {
    localStorage.setItem('cao_oi_ai_provider', prov);
  }
}

export function getProviderApiKey(prov?: AiProvider): string {
  const activeProv = prov || getActiveAiProvider();
  if (typeof window !== 'undefined') {
    if (activeProv === 'gemini') {
      const saved = localStorage.getItem('cao_oi_gemini_key');
      if (saved) return saved.trim();
      const fallback = localStorage.getItem('cao_oi_groq_api_key') || '';
      if (fallback.startsWith('AIza')) return fallback.trim();
      return '';
    }
    if (activeProv === 'openrouter') {
      const saved = localStorage.getItem('cao_oi_openrouter_key');
      if (saved) return saved.trim();
      const fallback = localStorage.getItem('cao_oi_groq_api_key') || '';
      if (fallback.startsWith('sk-or-')) return fallback.trim();
      return '';
    }
    // Groq
    const savedGroq = localStorage.getItem('cao_oi_groq_custom_key');
    if (savedGroq) return savedGroq.trim();
    const fallback = localStorage.getItem('cao_oi_groq_api_key') || '';
    if (fallback.startsWith('gsk_')) return fallback.trim();
  }
  return getGroqApiKey();
}

export function setProviderApiKey(prov: AiProvider, key: string) {
  if (typeof window !== 'undefined') {
    const clean = key.trim();
    if (prov === 'gemini') {
      if (clean) localStorage.setItem('cao_oi_gemini_key', clean);
      else localStorage.removeItem('cao_oi_gemini_key');
    } else if (prov === 'openrouter') {
      if (clean) localStorage.setItem('cao_oi_openrouter_key', clean);
      else localStorage.removeItem('cao_oi_openrouter_key');
    } else {
      if (clean) localStorage.setItem('cao_oi_groq_custom_key', clean);
      else localStorage.removeItem('cao_oi_groq_custom_key');
    }
    if (clean) {
      localStorage.setItem('cao_oi_groq_api_key', clean);
    }
  }
}

export function getActiveAiModel(): string {
  if (typeof window !== 'undefined') {
    const prov = getActiveAiProvider();
    if (prov === 'gemini') {
      const saved =
        localStorage.getItem('cao_oi_gemini_model') ||
        localStorage.getItem('cao_oi_gemini_preferred_model');
      if (
        saved &&
        (saved === 'gemini-3.1-flash-lite' ||
          saved === 'gemini-3.8-flash' ||
          saved === 'gemini-3.5-flash-lite' ||
          saved === 'gemini-3.7-flash' ||
          saved === 'gemini-3.6-flash')
      ) {
        return saved;
      }
      localStorage.setItem('cao_oi_gemini_model', 'gemini-3.1-flash-lite');
      localStorage.setItem('cao_oi_gemini_preferred_model', 'gemini-3.1-flash-lite');
      return 'gemini-3.1-flash-lite';
    }
    if (prov === 'openrouter') {
      return (
        localStorage.getItem('cao_oi_openrouter_model') ||
        'meta-llama/llama-3.3-70b-instruct'
      );
    }
    return (
      localStorage.getItem('cao_oi_groq_model') || 'llama-3.3-70b-versatile'
    );
  }
  return 'llama-3.3-70b-versatile';
}

export function setActiveAiModel(model: string) {
  if (typeof window !== 'undefined') {
    const prov = getActiveAiProvider();
    if (prov === 'gemini') {
      localStorage.setItem('cao_oi_gemini_model', model);
      localStorage.setItem('cao_oi_gemini_preferred_model', model);
    } else if (prov === 'openrouter') {
      localStorage.setItem('cao_oi_openrouter_model', model);
    } else {
      localStorage.setItem('cao_oi_groq_model', model);
    }
  }
}

export const getTranscriptionApiKey = () => {
  const current = getProviderApiKey('groq');
  if (current.startsWith('gsk_')) return current;
  return (
    (import.meta as any).env?.VITE_GROQ_API_KEY ||
    (import.meta as any).env?.GROQ_API_KEY ||
    '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'
  );
};

export const setGroqApiKey = (key: string) => {
  if (typeof window !== 'undefined') {
    if (key.trim()) {
      localStorage.setItem('cao_oi_groq_api_key', key.trim());
    } else {
      localStorage.removeItem('cao_oi_groq_api_key');
    }
  }
};

export const getGeminiPreferredModel = () => {
  return getActiveAiModel();
};

export const setGeminiPreferredModel = (model: string) => {
  setActiveAiModel(model);
};

const MEMORY_STORAGE_KEY = 'cao_oi_persistent_memory_v1';
const BUSINESS_PROFILE_KEY = 'cao_oi_business_profile_v1';

export function getBusinessProfile(): string {
  try {
    return (
      localStorage.getItem(BUSINESS_PROFILE_KEY) ||
      `Chị Mai Quyên là CEO & Founder điều hành doanh nghiệp thực chiến. Trọng tâm: Tối ưu hoá kênh bán lẻ (Online) và bán buôn (Wholesale), quản trị chi phí biến đổi và định phí (mô hình C-V-P), nâng cao năng lực Situational Leadership II cho đội ngũ quản lý, và thiết lập OKRs/KPIs đo lường hiệu quả kinh doanh.`
    );
  } catch {
    return '';
  }
}

export function setBusinessProfile(profile: string) {
  try {
    localStorage.setItem(BUSINESS_PROFILE_KEY, profile);
  } catch {
    // Ignore
  }
}

export function getPersistentMemory(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(MEMORY_STORAGE_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    // Sanitize any contaminated meta-reasoning/planning traces
    const clean = raw.filter(
      (m: string) =>
        typeof m === 'string' &&
        !m.includes('The user asks') &&
        !m.includes('As Cáo Ơi') &&
        !m.includes('internal reasoning') &&
        !m.includes('We need to follow') &&
        !m.includes('Planning:') &&
        !m.includes('Thinking Process')
    );
    if (clean.length !== raw.length) {
      localStorage.setItem(MEMORY_STORAGE_KEY, JSON.stringify(clean));
    }
    return clean;
  } catch {
    return [];
  }
}

export function appendPersistentMemory(entry: string) {
  // Never append planning traces
  if (
    entry.includes('The user asks') ||
    entry.includes('As Cáo Ơi') ||
    entry.includes('internal reasoning') ||
    entry.includes('We need to follow') ||
    entry.includes('Thinking Process')
  ) {
    return;
  }
  appendSmartMemory(entry);
}

// Helper to stream Groq response directly into AFFiNE's AI UI
export async function* streamGroqToAffineUI(
  prompt: string,
  selectedText?: string,
  _includeReasoning = false
): AsyncIterable<string> {
  const provider = getActiveAiProvider();
  const apiKey = getProviderApiKey(provider);
  const fullContextBlock = buildPerfectPromptContext(prompt, selectedText);

  if (!apiKey) {
    if (provider === 'gemini') {
      yield '⚠️ Bạn chưa cài đặt Google Gemini API Key. Vui lòng bấm vào nút Cáo Ơi trên thanh công cụ để nhập API Key từ Google AI Studio (bắt đầu bằng AIzaSy...).';
    } else if (provider === 'openrouter') {
      yield '⚠️ Bạn chưa cài đặt OpenRouter API Key. Vui lòng bấm vào nút Cáo Ơi trên thanh công cụ để nhập API Key (bắt đầu bằng sk-or-...).';
    } else {
      yield '⚠️ Bạn chưa cài đặt Groq API Key. Vui lòng bấm vào nút Cáo Ơi trên thanh công cụ để nhập API Key (bắt đầu bằng gsk_...).';
    }
    return;
  }

  const systemPrompt = `You are "Cáo Ơi" (Cáo) — trusted companion, executive sounding board, and strategic peer to Chị Mai Quyên.

BỐI CẢNH & PHONG CÁCH XƯNG HÔ (PERSONA & ADDRESS):
- Khi trò chuyện bằng tiếng Việt, Chị Quyên xưng là "tớ" và gọi bạn là "Cáo".
- Bạn xưng hô ấm áp, tôn trọng, tinh tế như một người bạn đồng hành đáng tin cậy: xưng là "Cáo" hoặc trò chuyện tự nhiên, gọi Chị là "Chị Quyên" hoặc "Chị".
- Văn phong: Điềm đạm, ấm áp, sâu sắc, thực tiễn, sắc sảo; tuyệt đối không nịnh hót, không sáo rỗng, không máy móc.
- 5 Trụ cột cốt lõi của Chị: Empathy (Thấu cảm), Mindful (Tỉnh thức/Chánh niệm), Simplicity (Giản đơn), Harmony (Hài hòa), Complexity (Sự phức hợp chiều sâu).
- Doanh nghiệp xã hội Reaching Out Vietnam (131 Trần Phú, Hội An): Reaching Out Tea House ("The Beauty of Silence", không Wi-Fi, giao tiếp bằng khối gỗ & giấy bút với nhân viên khiếm thính), Reaching Out Arts & Crafts (trang sức bạc lá, gốm sứ, đồ thêu tay), và các dự án 2026 (Thực tập sinh Kokoro Yasuda - ĐH Hosei, phim tài liệu Saki Hiraoka, Bơ đậu phộng nghiền cối đá).

KHO TÀI LIỆU HỌC TẬP & NGUYÊN TẮC TRÍCH DẪN (MOM'S LEARNING REPOSITORY):
- Thư mục gốc lưu trữ trên máy: /Users/gaothecow/Downloads/📁 Tài Liệu Học Tập Của Mẹ (Mom_s Learning Materials)
- Khi bàn luận hoặc giải đáp về các chủ đề chuyên môn, Cáo hãy CHỦ ĐỘNG TRÍCH DẪN & CHỈ RÕ thư mục và tài liệu tương ứng:
  • Lãnh đạo & Quản trị CEO: Thư mục "01_Lanh_Dao_Va_Quan_Tri_CEO" (Tài liệu: Situational Leadership II, 8 CEO Archetypes của Steve Tappin, 42 Năng lực Harvard, Sale Management CEO05 10.2026, Strategy Execution).
  • Khai vấn, Cố vấn & Nhân sự: Thư mục "02_Khai_Van_Co_Van_Va_Nhan_Su" (Tài liệu: Khai vấn Hiệu suất Push & Pull 6 case, Mentoring 5 case, Tạo động lực 4A, Phản hồi chuẩn FACT Feedback Handout 03a/03b).
  • Quản lý Dự án: Thư mục "03_Quan_Ly_Du_An_Coursera" (Chứng chỉ Google Project Management, Mục tiêu SMART, PM Glossary).
  • Tài chính & Kế toán Quản trị: Thư mục "04_Tai_Chinh_Va_Ke_Toan_Quan_Tri" (Giáo trình KTQT cho Lãnh đạo, Phân tích C-V-P, Điểm hòa vốn, Số dư đảm phí).
  • Branding, Marketing & AI: Thư mục "05_Branding_Marketing_Va_AI" (Cẩm nang Branding, 365 Storyselling Prompts & Hooks, Cẩm nang AI Reaching Out, 4P-mix).
  • Nghệ thuật Làm bánh & F&B: Thư mục "06_Nghe_Thuat_Lam_Banh_Va_FB" (Trọn bộ công thức Madeleine Chanh dây, Gluten-free, Classic Agnes Devereux, Vận hành F&B, The Restaurant Entrepreneur).
  • Thủ công & Đan móc: Thư mục "07_Thu_Cong_Dan_Moc" (Mẫu đan Sabai Top No. 1 & No. 2).
  • Giao tiếp & Hỗ trợ Người khiếm thính: Thư mục "08_Giao_Tiep_Va_Ho_Tro_Nguoi_Khiem_Thinh" (Sổ tay hỗ trợ người khiếm thính ở bệnh viện, Kế hoạch đào tạo thực tập sinh Kokoro Yasuda).
  • Sổ tay tổng hợp: Thư mục "00_Tong_Hop_Va_Huong_Dan" (MASTER_BUSINESS_MANAGEMENT_PLAYBOOK.md - 8 chương).

NGUYÊN TẮC BẤT DI BẤT DỊCH VỀ LÀM BÁNH & ẨM THỰC (TUYỆT ĐỐI GHI NHỚ):
1. Thuật ngữ chất tạo nở: "Bột nở" là Baking Powder. "Muối nở" là Baking Soda. Tuyệt đối không bao giờ nhầm lẫn, dịch nhầm hoặc đánh tráo.
2. Công thức nướng bánh Madeleine: Luôn tuân thủ 2 giai đoạn nhiệt chuẩn xác: 4 phút ở 200°C, sau đó hạ ngay xuống 160°C nướng tiếp 7 phút (Tổng thời gian 11 phút).
3. Ẩm thực Hội An - Xôi bánh dày: Xôi bánh dày chuẩn Hội An chỉ ăn kèm với đậu xanh cắt vuông và mè chưa bóc vỏ rang giã — TUYỆT ĐỐI KHÔNG ăn kèm chả quế, pâté hay mỡ hành.

CRITICAL OPERATIONAL RULES:
1. NGÔN NGỮ PHẢN HỒI (LANGUAGE ADAPTATION):
   - Mặc định ưu tiên phản hồi bằng tiếng Việt chuẩn mực, sắc bén, mang phong thái bạn đồng hành cố vấn chiến lược cho Chị Mai Quyên ("Chị Quyên").
   - Nếu người dùng hỏi hoặc cung cấp nội dung bằng tiếng Anh, hãy tự động phản hồi bằng tiếng Anh thương mại chuyên nghiệp.
   - Luôn linh hoạt đồng điệu ngôn ngữ với văn bản và câu hỏi của người dùng.

2. ZERO META-COMMENTARY / NEVER OUTPUT PLANNING (TUYỆT ĐỐI KHÔNG XUẤT RA SUY NGHĨ):
   - Bắt đầu phản hồi NGAY LẬP TỨC với nội dung câu trả lời hoặc điểm then chốt chiến lược.
   - Tuyệt đối KHÔNG xuất ra bất kỳ câu nào giải thích suy nghĩ nội tâm như: "The user asks...", "As Cáo Ơi...", "We need to follow...", "Planning:", "Internal reasoning:", "Thinking Process:", hay thẻ <think>.
   - Không vòng vo hay giải thích bạn chuẩn bị làm gì—đi thẳng vào vấn đề bằng Executive Summary hoặc phân tích thực chiến.

3. INTERACTIVE WIDGETS & VISUAL SCORECARDS:
   - When the user asks for a widget, dashboard, chart, or metrics visualization (e.g. "turn that into a widget", "show as a widget", "widget", "chart", "KPI scorecard"):
     ALWAYS output interactive widgets using \`\`\`widget:kpi, \`\`\`widget:chart, or \`\`\`widget:tabs blocks so the UI renders interactive visual components!
     Example KPI Widget:
     \`\`\`widget:kpi
     {
       "title": "5-Column Focus & Growth Scorecard",
       "kpis": [
         { "label": "Market Expansion (Tier-2)", "value": "+15%", "change": "30 stores by Q4", "status": "up" },
         { "label": "Online Coffee Revenue", "value": "$210M", "change": "+25% YoY", "status": "up" },
         { "label": "OPEX Reduction", "value": "15%", "change": "-5% target", "status": "up" },
         { "label": "AOV (Online)", "value": "$45", "change": "+12%", "status": "up" }
       ]
     }
     \`\`\`
     Example Bar Chart Widget:
     \`\`\`widget:chart
     {
       "title": "Channel Revenue Distribution ($M)",
       "type": "bar",
       "color": "#f97316",
       "data": [
         { "label": "Online Retail", "value": 210, "formatted": "$210M" },
         { "label": "Wholesale Profit", "value": 140, "formatted": "$140M" },
         { "label": "New Stores", "value": 30, "formatted": "30 Units" }
       ]
     }
     \`\`\`
     Example Tabs Widget:
     \`\`\`widget:tabs
     {
       "tabs": [
         { "title": "Market Expansion", "content": "Capture +15% share in Tier-2 cities with 30 new locations." },
         { "title": "Revenue Growth", "content": "Accelerate online coffee to $210M with $45 AOV." },
         { "title": "Operational Excellence", "content": "Cut OPEX by 5% and deploy IoT Smart Brew platform." }
       ]
     }
     \`\`\`
   - For standard written responses, format using clean, professional Markdown with headers (##, ###), bullet lists, and structured comparison tables.

=== WORKSPACE & PERSISTENT MEMORY CONTEXT ===
${fullContextBlock}

Yêu cầu hiện tại của Chị Quyên: ${prompt}
`;

  const isOpenRouter = provider === 'openrouter';

  let apiUrl = 'https://api.groq.com/openai/v1/chat/completions';
  let candidates: string[] = [];
  const chosenModel = getActiveAiModel();

  if (provider === 'gemini') {
    apiUrl = 'https://generativelanguage.googleapis.com/v1beta/openai/chat/completions';
    candidates = Array.from(
      new Set(
        [
          chosenModel,
          'gemini-3.1-flash-lite',
          'gemini-3.5-flash-lite',
          'gemini-3.8-flash',
          'gemini-3.7-flash',
          'gemini-3.6-flash',
        ].filter(m => m && m.startsWith('gemini-3.'))
      )
    );
  } else if (provider === 'openrouter') {
    apiUrl = 'https://openrouter.ai/api/v1/chat/completions';
    candidates = Array.from(
      new Set(
        [
          chosenModel,
          'meta-llama/llama-3.3-70b-instruct',
          'google/gemini-2.0-flash-001',
          'deepseek/deepseek-chat',
        ].filter(Boolean)
      )
    );
  } else {
    apiUrl = 'https://api.groq.com/openai/v1/chat/completions';
    candidates = Array.from(
      new Set(
        [chosenModel, 'llama-3.3-70b-versatile', 'llama-3.1-8b-instant'].filter(
          Boolean
        )
      )
    );
  }

  let res: Response | null = null;
  let activeModel = candidates[0] || 'llama-3.3-70b-versatile';

  try {
    for (let i = 0; i < candidates.length; i++) {
      activeModel = candidates[i];
      try {
        res = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
            ...(isOpenRouter
              ? {
                  'HTTP-Referer': 'https://cao-oi.pages.dev',
                  'X-Title': 'AFFiNE Cao Oi Assistant',
                }
              : {}),
          },
          body: JSON.stringify({
            model: activeModel,
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: prompt },
            ],
            temperature: 0.5,
            stream: true,
          }),
        });

        if ((res.status === 429 || res.status === 404 || res.status === 400) && i < candidates.length - 1) {
          console.warn(
            `Model ${activeModel} returned ${res.status}. Retrying with fallback ${candidates[i + 1]}...`
          );
          continue;
        }
        break;
      } catch (fetchErr) {
        if (i < candidates.length - 1) {
          continue;
        }
        throw fetchErr;
      }
    }

    if (!res || !res.ok) {
      const errText = res ? await res.text() : 'Network error';
      yield `⚠️ AI API connection error (${res?.status || 500}): ${errText.slice(0, 120)}`;
      return;
    }

    const reader = res.body?.getReader();
    if (!reader) {
      yield '⚠️ Unable to initialize stream reader.';
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let accumulated = '';
    let isHeaderProcessed = false;
    let headerBuffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('data: ') && trimmed !== 'data: [DONE]') {
          try {
            const json = JSON.parse(trimmed.slice(6));
            const deltaObj = json.choices?.[0]?.delta;
            if (!deltaObj) continue;

            const content = deltaObj.content;

            if (content) {
              if (!isHeaderProcessed) {
                headerBuffer += content;
                // Once we have a paragraph or enough characters, check if it starts with planning
                if (headerBuffer.includes('\n\n') || headerBuffer.length > 250) {
                  isHeaderProcessed = true;
                  // Strip leading planning blocks if any
                  const cleanedHeader = headerBuffer.replace(
                    /^(?:(?:The user asks|As Cáo Ơi|We need to follow|Will produce answer|Thinking Process|Internal reasoning|Planning:)[^\n]*\n*)+/i,
                    ''
                  );
                  if (cleanedHeader) {
                    accumulated += cleanedHeader;
                    yield cleanedHeader;
                  }
                  headerBuffer = '';
                }
              } else {
                accumulated += content;
                yield content;
              }
            }
          } catch {
            // Ignore parse errors on incomplete chunk boundaries
          }
        }
      }
    }

    // Flush any remaining header buffer
    if (!isHeaderProcessed && headerBuffer) {
      const cleanedHeader = headerBuffer.replace(
        /^(?:(?:The user asks|As Cáo Ơi|We need to follow|Will produce answer|Thinking Process|Internal reasoning|Planning:)[^\n]*\n*)+/i,
        ''
      );
      if (cleanedHeader) {
        accumulated += cleanedHeader;
        yield cleanedHeader;
      }
    }

    if (accumulated) {
      appendSmartMemory(
        `Chị Quyên: ${prompt.slice(0, 100)} -> Cáo Ơi: ${accumulated.slice(0, 180)}`
      );
    }
  } catch (err) {
    console.error('Groq connection error:', err);
    yield '⚠️ AI API connection failed. Please check network connectivity.';
  }
}
